import {CsvMachineError, CsvMachineErrorList} from "src/app/model/custom/import-csv/csv-machine-error";
import * as moment from "moment";
import {DatePipe} from "@angular/common";
import {MachinePropertiesErrors} from "./machine-properties-errors";
import {TranslocoService} from "@ngneat/transloco";
import {CsvMachineData} from "src/app/model/custom/import-csv/csv-machine-data";
import {MachinePersistRequestCsv} from "../../model/requests/machine-persist-request-csv";

export class ImportCsvMachineErrors {

  public errors: CsvMachineErrorList;

  private readonly DATE_FORMAT: string = 'YYYY-MM-DD';
  private readonly LATITUDE: string = 'latitude';
  private readonly LONGITUDE: string = 'longitude';
  private readonly ORGANIZATION_CODE: string = 'orgCode';
  private readonly PARENT_CODE: string = 'parentCode';
  private readonly START_DATE: string = 'startDate';
  private readonly END_DATE: string = 'endDate';

  private machinePropertiesErrors: MachinePropertiesErrors = new MachinePropertiesErrors(this.i18n);


  constructor(
    private readonly i18n: TranslocoService,
    private readonly datePipe: DatePipe
  ) {
    this.errors = {}
  }

  public required(machine: MachinePersistRequestCsv | CsvMachineData, objectProperty: string, errorProperty: string = objectProperty): boolean {
    if (machine[objectProperty] == null || machine[objectProperty].toString().trim() == '') {
      this.addCsvMachineError(errorProperty, {
        message: 'isRequired',
        params: {
          field: this.machinePropertiesErrors.getTranslation(errorProperty).toLowerCase(),
          value: machine[objectProperty],
        }
      });
      return false;
    }
    return true;
  }

  public notRequired(machine: MachinePersistRequestCsv | CsvMachineData, objectProperty: string, errorProperty: string = objectProperty): boolean {
    if (machine[objectProperty] != null && machine[objectProperty].toString().trim() != '') {
      this.addCsvMachineError(errorProperty, {
        message: 'isNotRequired',
        params: {
          field: this.machinePropertiesErrors.getTranslation(errorProperty).toLowerCase(),
          value: machine[objectProperty],
        }
      });
      return false;
    }
    return true;
  }

  public isFloatNumber(machine: MachinePersistRequestCsv, property: string): boolean {
    const nameRegexp: RegExp = /^[0-9.+-]+$/;
    if (machine[property] == null || !nameRegexp.test(machine[property])) {
      this.addCsvMachineError(property, {
        message: 'needsToBeANumber',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: machine[property]
        }
      });
      return false;
    }
    return true;
  }

  public isPositiveInteger(machine: MachinePersistRequestCsv, property: string): boolean {
    const nameRegexp: RegExp = /^\d+$/;
    if (machine[property] == null || !nameRegexp.test(machine[property]) || Number(machine[property]) < 0) {
      this.addCsvMachineError(property, {
        message: 'needsToBeAnInteger',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: machine[property]
        }
      });
      return false;
    }
    return true;
  }

  public minLength(machine: MachinePersistRequestCsv, property: string, minLength: number): void {
    if (machine[property] == null || machine[property].length < minLength) {
      this.addCsvMachineError(property, {
        message: 'noMinLength',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: machine[property],
        }
      });
    }
    return null;
  }

  public maxLength(machine: MachinePersistRequestCsv, property: string, maxLength: number): void {
    if (machine[property] == null || machine[property].length > maxLength) {
      this.addCsvMachineError(property, {
        message: 'exceedsMaxLength',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: machine[property]
        }
      });
    }
    return null;
  }

  public length(machine: MachinePersistRequestCsv, property: string, length: number): void {
    if (machine[property] == null || machine[property].length != length) {
      this.addCsvMachineError(property, {
        message: 'noValidLength',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: machine[property]
        }
      });
    }
    return null;
  }

  public noSpecialCharacters(machine: MachinePersistRequestCsv, property: string): void {
    const nameRegexp: RegExp = /[!@#$%^&*()+=[\]{};':"\\|,.<>/?]/;
    if (machine[property] == null || nameRegexp.test(machine[property])) {
      this.addCsvMachineError(property, {
        message: 'hasSpecialChars',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: machine[property],
        }
      });
    }
    return null;
  }

  public noWhitespaces(machine: MachinePersistRequestCsv, property: string): void {
    const nameRegexp: RegExp = /\s/;
    if (machine[property] == null || nameRegexp.test(machine[property])) {
      this.addCsvMachineError(property, {
        message: 'hasWhitespaces',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: machine[property]
        }
      });
    }
    return null;
  }

  public foundInData<T>(machine: MachinePersistRequestCsv, property: string, data: { [key: string]: T }): void {
    if (machine[property] == null || data[machine[property]] == null) {
      this.addCsvMachineError(property, {
        message: 'notFoundInData',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: machine[property]
        }
      });
    }
    return null;
  }

  public foundOrganization<T>(machine: MachinePersistRequestCsv, data: { [key: string]: T }): void {
    if (machine[this.ORGANIZATION_CODE] == null || machine[this.PARENT_CODE] == null ||
      data[machine[this.ORGANIZATION_CODE] + '.' + machine[this.PARENT_CODE]] == null) {
      this.addCsvMachineError('organization', {
        message: 'notFoundInData',
        params: {
          field: this.machinePropertiesErrors.getTranslation('orgCodeAndParentCode').toLowerCase(),
          value: machine[this.ORGANIZATION_CODE]
        }
      });
    }
    return null;
  }

  public validValues(machine: MachinePersistRequestCsv, property: string, values: any[]): void {
    if (machine[property] == null || !values.includes(machine[property])) {
      this.addCsvMachineError(property, {
        message: property + 'NoValidValue',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: machine[property]
        }
      });
    }
    return null;
  }

  public validDate(machine: MachinePersistRequestCsv, property: string): boolean {
    if (machine[property] == null || !moment(machine[property], this.DATE_FORMAT).isValid()) {
      this.addCsvMachineError(property, {
        message: 'noValidDate',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: machine[property],
        }
      });
      return false;
    }
    return true;
  }

  public dateInLastNYears(machine: MachinePersistRequestCsv, property: string, n: number): void {
    if (machine[property] == null || !moment(machine[property], this.DATE_FORMAT).isValid()
      || ImportCsvMachineErrors.subtractYears(n) > moment(machine[property], this.DATE_FORMAT).toDate().getTime()) {
      this.addCsvMachineError(property, {
        message: 'isNotFromLastNYears',
        params: {
          field: this.machinePropertiesErrors.getTranslation(property).toLowerCase(),
          value: this.datePipe.transform(moment(machine[property], this.DATE_FORMAT).toDate()),
          years: n
        }
      });
    }
    return null;
  }

  public validLatitude(machine: MachinePersistRequestCsv): void {
    let latitude = Number.parseFloat(machine[this.LATITUDE]);
    if (machine[this.LATITUDE] == null || (latitude < -90) && (latitude > 90)) {
      this.addCsvMachineError(this.LATITUDE, {
        message: 'noValidLatitude',
        params: {
          field: this.machinePropertiesErrors.getTranslation(this.LATITUDE).toLowerCase(),
          value: machine[this.LATITUDE],
        }
      });
    }
    return null;
  }

  public validLongitude(machine: MachinePersistRequestCsv): void {
    let longitude = Number.parseFloat(machine[this.LONGITUDE]);
    if (machine[this.LONGITUDE] == null || (longitude < -180) && (longitude > 180)) {
      this.addCsvMachineError(this.LONGITUDE, {
        message: 'noValidLongitude',
        params: {
          field: this.machinePropertiesErrors.getTranslation('longitude').toLowerCase(),
          value: machine[this.LONGITUDE],
        }
      });
    }
    return null;
  }

  public subscriptionDatesRange(machine: MachinePersistRequestCsv): void {
    if (machine[this.START_DATE] == null || machine[this.END_DATE] == null ||
      moment(machine[this.END_DATE], this.DATE_FORMAT).toDate().getTime() < moment(machine[this.START_DATE], this.DATE_FORMAT).toDate().getTime()) {
      this.addCsvMachineError(this.START_DATE, {
        message: 'invalidSubscriptionDatesRange',
        params: {
          field: this.machinePropertiesErrors.getTranslation(this.START_DATE).toLowerCase(),
          value: this.datePipe.transform(moment(machine[this.START_DATE], this.DATE_FORMAT).toDate()),
        }
      });
    }
    return null;
  }

  private addCsvMachineError(property, csvMachineError: CsvMachineError): void {
    if (!this.errors.hasOwnProperty(property)) this.errors[property] = [];
    this.errors[property].push(csvMachineError);
  }

  private static subtractYears(numOfYears: number, dateTime: number = new Date().getTime()): number {
    let date = new Date(dateTime);
    date.setFullYear(date.getFullYear() - numOfYears);
    return date.getTime();
  }
}
